import api from './api.ts';

export interface AdminOverview {
  users: {
    total: number;
    last7Days: number;
    last30Days: number;
    recent: Array<{
      id: string;
      email: string;
      name: string;
      provider?: string;
      createdAt: string;
    }>;
  };
  meetings: {
    available: boolean;
    total: number;
    live: number;
    byStatus: Record<string, number>;
    note?: string;
  };
  host: {
    platform: string;
    arch: string;
    hostname: string;
    uptimeSeconds: number;
    processUptimeSeconds: number;
    cpuCount: number;
    cpuModel: string;
    loadAverage: { '1m': number; '5m': number; '15m': number };
    memory: {
      totalBytes: number;
      freeBytes: number;
      usedBytes: number;
      usedPercent: number;
    };
    process: {
      rssBytes: number;
      heapUsedBytes: number;
      heapTotalBytes: number;
    };
    nodeVersion: string;
  };
  disk: {
    mounts: Array<{
      path: string;
      totalBytes?: number;
      usedBytes?: number;
      availableBytes?: number;
      usedPercent?: number;
      error?: string;
    }>;
  };
  generatedAt: string;
}

export interface AdminLiveStats {
  activeRooms: number;
  totalParticipants: number;
  rooms: Array<{
    meetingId: string;
    participantCount: number;
    participants: Array<{ socketId: string; userName: string }>;
  }>;
  generatedAt: string;
}

export interface SystemLogItem {
  id: number;
  level: string;
  source: string;
  message: string;
  meta?: Record<string, unknown> | null;
  createdAt: string;
}

export interface AdminUserRow {
  id: string;
  email: string;
  name: string;
  provider?: string | null;
  avatar?: string | null;
  createdAt: string;
  updatedAt?: string;
}

export interface AdminUsersPage {
  total: number;
  limit: number;
  offset: number;
  page: number;
  totalPages: number;
  items: AdminUserRow[];
}

export interface AdminEntry {
  email: string;
  source: 'env' | 'db';
  note?: string | null;
  createdAt?: string | null;
  id?: string;
}

function formatBytes(bytes?: number): string {
  if (bytes == null || Number.isNaN(bytes)) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let n = bytes;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i += 1;
  }
  return `${n.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

function formatUptime(seconds: number): string {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}g ${h}s`;
  if (h > 0) return `${h}s ${m}dk`;
  return `${m} dk`;
}

export const adminService = {
  formatBytes,
  formatUptime,

  async checkStatus(): Promise<boolean> {
    try {
      const res = await api.get('/api/admin/status');
      return !!res.data?.data?.isAdmin;
    } catch {
      return false;
    }
  },

  async getOverview(): Promise<AdminOverview> {
    const res = await api.get('/api/admin/overview');
    if (!res.data?.success) throw new Error(res.data?.error || 'Overview alınamadı');
    return res.data.data;
  },

  async listUsers(params?: {
    page?: number;
    limit?: number;
    q?: string;
  }): Promise<AdminUsersPage> {
    const res = await api.get('/api/admin/users', { params });
    if (!res.data?.success) throw new Error(res.data?.error || 'Kullanıcı listesi alınamadı');
    return res.data.data;
  },

  async deleteUser(userId: string): Promise<void> {
    const res = await api.delete(`/api/admin/users/${encodeURIComponent(userId)}`);
    if (!res.data?.success) throw new Error(res.data?.error || 'Kullanıcı silinemedi');
  },

  async getLiveStats(): Promise<AdminLiveStats | null> {
    try {
      const res = await api.get('/api/meetings/admin/live');
      if (!res.data?.success) return null;
      return res.data.data;
    } catch {
      return null;
    }
  },

  async getLogs(params?: {
    limit?: number;
    offset?: number;
    level?: string;
    q?: string;
  }): Promise<{ total: number; items: SystemLogItem[] }> {
    const res = await api.get('/api/admin/logs', { params });
    if (!res.data?.success) throw new Error(res.data?.error || 'Loglar alınamadı');
    return res.data.data;
  },

  async appendLog(message: string, level: string = 'info'): Promise<void> {
    await api.post('/api/admin/logs', { message, level, source: 'admin-ui' });
  },

  async listAdmins(): Promise<AdminEntry[]> {
    const res = await api.get('/api/admin/admins');
    if (!res.data?.success) throw new Error(res.data?.error || 'Admin listesi alınamadı');
    return res.data.data;
  },

  async addAdmin(email: string, note?: string): Promise<void> {
    const res = await api.post('/api/admin/admins', { email, note });
    if (!res.data?.success) throw new Error(res.data?.error || 'Admin eklenemedi');
  },

  async removeAdmin(email: string): Promise<void> {
    const res = await api.delete(`/api/admin/admins/${encodeURIComponent(email)}`);
    if (!res.data?.success) throw new Error(res.data?.error || 'Admin silinemedi');
  },
};
